import { Link } from 'react-router-dom';
import Button from '@/components/common/Button';
import StaticPageLayout from '@/components/layout/StaticPageLayout';
import bioImg from '@/images/tripp_and_randy.png';
import distressedImg from '@/images/distressed.png';
import jorgesImg from '@/images/jorges.png';

export default function AboutPage() {
  return (
    <StaticPageLayout>
      {/* ============ HERO / INTRO SECTION ============ */}
      <section className="relative z-[6] -mt-[50px] bg-[#111] bg-cover bg-center px-6 pb-[calc(100px+50px)] pt-[calc(100px+50px)] text-center md:-mt-[80px] md:px-10 md:pb-[calc(100px+80px)] md:pt-[calc(100px+80px)] [clip-path:polygon(0_50px,100%_0,100%_calc(100%-50px),0_100%)] md:[clip-path:polygon(0_80px,100%_0,100%_calc(100%-80px),0_100%)]"
      style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.9), rgba(0,0,0,0.9)), url(${jorgesImg})` }}
      >
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="font-heading text-[48px] uppercase tracking-[2px] text-primary-600 md:text-[85px]">
            ABOUT US
          </h2>
          <p className="mt-4 text-lg font-semibold text-primary-600">
            Built by people who've actually been in it
          </p>

          <div className="mt-12 flex flex-col lg:flex-row lg:items-center lg:gap-12">
            {/* Text Content */}
            <div className="lg:flex-[2]">
              <p className="text-lg text-neutral-700">
                BandJerry was founded by two guys with wildly different, but oddly
                complementary, resumes.
              </p>
              <p className="mt-6 text-lg leading-relaxed text-neutral-600">
                A touring musician/marketing executive and an AI engineer. Both in a
                band together. Writers of music and lyrics, code and cold emails to
                venues.
              </p>
              <p className="mt-6 text-lg leading-relaxed text-neutral-600">
                We've played empty rooms, packed theaters,{' '}
                <em>and</em> corporate boardrooms. We put in years learning what
                actually helps a band grow and merged those experiences with the
                expertise we got from our day jobs.
              </p>
            </div>

            {/* Image */}
            <div className="mt-12 flex justify-center lg:mt-0 lg:flex-1 lg:self-stretch lg:items-center">
              <img
                src={bioImg}
                alt="BandJerry founders"
                className="w-1/2 h-auto rounded-lg shadow-xl lg:w-full lg:max-h-full lg:object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHAT BANDJERRY HELPS WITH ============ */}
      <section
        className="relative -mt-[50px] bg-[#222] bg-cover bg-center px-6 pb-[calc(100px+50px)] pt-[calc(100px+50px)] md:-mt-[80px] md:pb-[calc(100px+80px)] md:pt-[calc(100px+80px)] [clip-path:polygon(0_50px,100%_0,100%_calc(100%-50px),0_100%)] md:[clip-path:polygon(0_80px,100%_0,100%_calc(100%-80px),0_100%)]"
        style={{ backgroundImage: `url(${distressedImg})` }}
      >
        <div className="mx-auto max-w-4xl">
          <h2 className="font-heading text-[48px] uppercase tracking-[2px] text-primary-600 md:text-[75px]">
            BANDJERRY HELPS WITH THE WORK OUTSIDE THE MUSIC
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-white">
            BandJerry isn't a creative partner. It won't tell you how your music
            should sound, upscale your promo shot, rewrite your identity or
            manufacture your brand.
          </p>
          <p className="mt-4 text-lg leading-relaxed text-white">We focus on logistical moves:</p>

          <ul className="mt-8 space-y-4">
            {[
              'Finding opportunities',
              'Building momentum',
              'Reaching the right people',
              'Getting in better rooms',
              'Helping more fans discover your music',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <svg
                  className="mt-1 h-5 w-5 flex-shrink-0 text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-lg text-white">{item}</span>
              </li>
            ))}
          </ul>

          <p className="mt-8 text-lg font-semibold text-white">
            You make the creative decisions. BandJerry helps with the next move.
          </p>
        </div>
      </section>

      {/* ============ OUR PHILOSOPHY ============ */}
      <section className="relative -mt-[50px] bg-[#111] px-6 pb-[calc(100px+50px)] pt-[calc(100px+50px)] md:-mt-[80px] md:pb-[calc(100px+80px)] md:pt-[calc(100px+80px)] [clip-path:polygon(0_50px,100%_0,100%_calc(100%-50px),0_100%)] md:[clip-path:polygon(0_80px,100%_0,100%_calc(100%-80px),0_100%)]">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-heading text-[48px] uppercase tracking-[2px] text-primary-600 md:text-[75px]">
            OUR PHILOSOPHY
          </h2>

          <div className="mt-12 grid gap-8 sm:grid-cols-2">
            <div className="rounded-lg border border-neutral-300 bg-neutral-200 p-6">
              <h3 className="font-heading text-2xl uppercase text-primary-600">
                SPECIFIC OVER GENERIC
              </h3>
              <p className="mt-2 text-lg text-neutral-600">
                If advice could apply to anyone, it probably won't help anyone.
              </p>
            </div>

            <div className="rounded-lg border border-neutral-300 bg-neutral-200 p-6">
              <h3 className="font-heading text-2xl uppercase text-primary-600">
                DIRECTION OVER INFORMATION
              </h3>
              <p className="mt-2 text-lg text-neutral-600">
                Musicians don't need more "content" to consume. They need clear
                next steps.
              </p>
            </div>

            <div className="rounded-lg border border-neutral-300 bg-neutral-200 p-6">
              <h3 className="font-heading text-2xl uppercase text-primary-600">
                MOMENTUM OVER PERFECTION
              </h3>
              <p className="mt-2 text-lg text-neutral-600">
                Overthinking kills progress. Action creates opportunities. Take
                risks, fail fast, keep going.
              </p>
            </div>

            <div className="rounded-lg border border-neutral-300 bg-neutral-200 p-6">
              <h3 className="font-heading text-2xl uppercase text-primary-600">
                LEAVE ART TO THE ARTISTS
              </h3>
              <p className="mt-2 text-lg text-neutral-600">
                The creative decisions belong to you. BandJerry exists only to
                support the work around the music.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQs ============ */}
      <section
        className="relative -mt-[50px] bg-[#222] bg-cover bg-center px-6 pb-[calc(100px+50px)] pt-[calc(100px+50px)] md:-mt-[80px] md:pb-[calc(100px+80px)] md:pt-[calc(100px+80px)] [clip-path:polygon(0_50px,100%_0,100%_calc(100%-50px),0_100%)] md:[clip-path:polygon(0_80px,100%_0,100%_calc(100%-80px),0_100%)]"
        style={{ backgroundImage: `url(${distressedImg})` }}
      >
        <div className="mx-auto max-w-4xl">
          <h2 className="font-heading text-[48px] uppercase tracking-[2px] text-primary-600 md:text-[75px]">
            FAQS
          </h2>

          <div className="mt-12 space-y-8">
            <div>
              <h3 className="font-heading text-2xl uppercase text-white">
                IS THIS JUST ANOTHER AI TOOL?
              </h3>
              <p className="mt-2 text-lg leading-relaxed text-neutral-600">
                No. We use AI models to provide fast, customized information, but
                the real-world experience of indie scene veterans shapes every Set
                and Move.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-2xl uppercase text-white">
                WILL THIS WORK IN MY CITY?
              </h3>
              <p className="mt-2 text-lg leading-relaxed text-neutral-600">
                Yes. Each set is tailored to your location and scene, from LA to
                Lincoln, Nebraska.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-2xl uppercase text-white">
                WHAT IF A SUGGESTED MOVE DOESN'T FEEL RIGHT TO ME?
              </h3>
              <p className="mt-2 text-lg leading-relaxed text-neutral-600">
                Press "Ignore" and leave feedback. Each Move gets more dialed in
                based on your feedback.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-2xl uppercase text-white">
                HOW MUCH TIME DOES THIS TAKE?
              </h3>
              <p className="mt-2 text-lg leading-relaxed text-neutral-600">
                Each Move is focused and manageable. Ideally a Move can be completed
                in one sitting. We built this for artists with real lives who don't
                have hours to spend on promotion.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-2xl uppercase text-white">
                WHO IS THE IDEAL USER?
              </h3>
              <p className="mt-2 text-lg leading-relaxed text-neutral-600">
                Indie artists serious about getting bigger—whether you're playing to
                20 people or 200.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BUT WHO IS JERRY? ============ */}
      <section className="relative -mt-[50px] bg-[#111] px-6 pb-32 pt-[calc(100px+50px)] md:-mt-[80px] md:pt-[calc(100px+80px)] [clip-path:polygon(0_50px,100%_0,100%_100%,0_100%)] md:[clip-path:polygon(0_80px,100%_0,100%_100%,0_100%)]">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-heading text-[48px] uppercase tracking-[2px] text-primary-600 md:text-[75px]">
            BUT WHO IS JERRY?
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-neutral-600">
            Jerry is inspired by Jerry Ellis, a mensch and business innovator who
            taught us that hard work and authenticity are the only ingredients in
            success. Everything else is showing off.
          </p>
          <div className="mt-10 flex items-center justify-center">
            <Link to="/signup">
              <Button size="lg">Start Free — Your First 3 Moves Are On Us</Button>
            </Link>
          </div>
        </div>
      </section>
    </StaticPageLayout>
  );
}
